import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Zap } from 'lucide-react';

/**
 * LoanOffersBanner
 * Single thin animated banner rotating through 6 loan offers with starting interest rates.
 * Blinking "Apply Now" button → routes to /services (loan & banking marketplace).
 *
 * Rates are competitive 2026 starting rates from major Indian banks — used as a teaser only.
 * Final approval depends on credit profile.
 */
const OFFERS = [
  { emoji: '🏠', loan: 'Home Loan',      rate: '8.40%', tenure: 'up to 30 yrs', highlight: '₹50L–₹5Cr',  color: '#0F766E' },
  { emoji: '💼', loan: 'Personal Loan',   rate: '10.49%', tenure: '1–5 yrs',     highlight: '24-hour disbursal', color: '#7C3AED' },
  { emoji: '🚗', loan: 'Car Loan',        rate: '8.65%', tenure: 'up to 7 yrs', highlight: '100% on-road', color: '#DC2626' },
  { emoji: '🎓', loan: 'Education Loan',  rate: '9.00%', tenure: 'up to 15 yrs',highlight: 'Moratorium incl.', color: '#0891B2' },
  { emoji: '🏪', loan: 'Business Loan',   rate: '11.99%', tenure: '1–7 yrs',     highlight: '₹5K–₹50L instant', color: '#EA580C' },
  { emoji: '🥇', loan: 'Gold Loan',       rate: '9.25%', tenure: '6m–36m',      highlight: '99% gold value',   color: '#CA8A04' },
];

const LoanOffersBanner = () => {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % OFFERS.length), 3500);
    return () => clearInterval(t);
  }, []);

  const o = OFFERS[idx];

  return (
    <div data-testid="loan-offers-banner" className="lob-wrap">
      <div className="lob-track">
        {/* MARQUEE — left scrolling text of all offers */}
        <div className="lob-marquee">
          {[...OFFERS, ...OFFERS].map((m, i) => (
            <span key={i} className="lob-marquee-item">
              <span style={{ fontSize: '1.1rem' }}>{m.emoji}</span>
              <strong>{m.loan}</strong>
              <span className="lob-rate">@ {m.rate} p.a.</span>
              <span className="lob-sep">·</span>
            </span>
          ))}
        </div>

        {/* HIGHLIGHTED CURRENT OFFER */}
        <div className="lob-cta-area">
          <div className="lob-current" style={{ '--accent': o.color }}>
            <span className="lob-emoji">{o.emoji}</span>
            <span className="lob-loan">{o.loan}</span>
            <span className="lob-rate-bg">@ {o.rate} p.a.</span>
            <span className="lob-divider" />
            <span className="lob-highlight">{o.highlight}</span>
          </div>

          <button
            onClick={() => navigate('/services')}
            data-testid="loan-banner-apply-now"
            className="lob-apply-btn"
          >
            <Zap size={14} className="lob-zap" /> Apply Now <ArrowRight size={14} />
          </button>
        </div>
      </div>

      <style>{`
        .lob-wrap {
          width: 100%;
          background: linear-gradient(90deg, #0F172A 0%, #1E293B 35%, #1E293B 65%, #0F172A 100%);
          border-bottom: 1px solid rgba(255,255,255,0.08);
          position: relative;
          overflow: hidden;
        }
        .lob-wrap::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: radial-gradient(circle at 30% 50%, rgba(255,107,53,0.12), transparent 60%),
                      radial-gradient(circle at 70% 50%, rgba(16,185,129,0.10), transparent 60%);
          pointer-events: none;
        }
        .lob-track {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.5rem;
          padding: 0.55rem 1.5rem;
          max-width: 1500px;
          margin: 0 auto;
          z-index: 1;
        }
        /* MARQUEE — runs left-to-right on the left half */
        .lob-marquee {
          display: flex;
          gap: 22px;
          overflow: hidden;
          flex: 1;
          mask-image: linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%);
          -webkit-mask-image: linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%);
          animation: lob-scroll 28s linear infinite;
        }
        @keyframes lob-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .lob-marquee-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #CBD5E1;
          font-size: 0.78rem;
          font-weight: 600;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .lob-marquee-item .lob-rate {
          color: #FDBA74;
          font-weight: 800;
        }
        .lob-marquee-item .lob-sep {
          color: rgba(255,255,255,0.25);
          margin: 0 4px;
        }
        /* HIGHLIGHTED CURRENT OFFER */
        .lob-cta-area {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
        }
        .lob-current {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 6px 14px;
          background: linear-gradient(135deg, var(--accent, #FF6B35), rgba(0,0,0,0.18));
          border: 1px solid rgba(255,255,255,0.18);
          border-radius: 999px;
          font-size: 0.82rem;
          color: white;
          font-weight: 700;
          white-space: nowrap;
          animation: lob-pop 3.5s ease-in-out infinite;
          box-shadow: 0 4px 14px rgba(0,0,0,0.25);
        }
        @keyframes lob-pop {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.03); }
        }
        .lob-emoji { font-size: 1.05rem; }
        .lob-rate-bg {
          background: rgba(255,255,255,0.18);
          padding: 2px 9px;
          border-radius: 999px;
          font-size: 0.78rem;
          letter-spacing: 0.02em;
        }
        .lob-divider {
          width: 1px;
          height: 16px;
          background: rgba(255,255,255,0.25);
        }
        .lob-highlight {
          font-size: 0.74rem;
          opacity: 0.95;
          font-weight: 500;
        }
        /* BLINKING APPLY NOW */
        .lob-apply-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 16px;
          background: linear-gradient(135deg, #FF6B35 0%, #F97316 100%);
          color: white;
          font-weight: 800;
          font-size: 0.82rem;
          border: 0;
          border-radius: 999px;
          cursor: pointer;
          letter-spacing: 0.02em;
          box-shadow: 0 0 0 0 rgba(255,107,53,0.7);
          animation: lob-blink 1.5s ease-in-out infinite;
          transition: transform .12s ease;
          white-space: nowrap;
        }
        .lob-apply-btn:hover { transform: translateY(-1px) scale(1.03); animation-play-state: paused; }
        @keyframes lob-blink {
          0%   { box-shadow: 0 0 0 0 rgba(255,107,53,0.65), 0 4px 12px rgba(255,107,53,0.4); filter: brightness(1); }
          50%  { box-shadow: 0 0 0 10px rgba(255,107,53,0), 0 6px 18px rgba(255,107,53,0.55); filter: brightness(1.18); }
          100% { box-shadow: 0 0 0 0 rgba(255,107,53,0), 0 4px 12px rgba(255,107,53,0.4); filter: brightness(1); }
        }
        .lob-zap { animation: lob-zap 1s ease-in-out infinite; }
        @keyframes lob-zap {
          0%, 100% { opacity: 1; transform: rotate(0); }
          50%      { opacity: 0.5; transform: rotate(-12deg); }
        }
        /* responsive */
        @media (max-width: 900px) {
          .lob-marquee { display: none; }
          .lob-track { justify-content: center; padding: 0.55rem 1rem; gap: 8px; }
          .lob-highlight { display: none; }
        }
        @media (max-width: 540px) {
          .lob-current { padding: 5px 10px; font-size: 0.76rem; gap: 6px; }
          .lob-divider { display: none; }
          .lob-rate-bg { font-size: 0.72rem; padding: 1px 7px; }
        }
      `}</style>
    </div>
  );
};

export default LoanOffersBanner;
